import { SiteUrls } from '@tableau/shared-urls';
import {
    ButtonType, ButtonWidget, CheckBoxWithLabelGroupWidget, CheckBoxWithLabelWidget,
    RadioButtonWithLabelGroupWidget, TextFieldWidget
} from '@tableau/widgets';
import { FormikProps, withFormik } from 'formik';
import * as React from 'react';
import * as _ from 'underscore';

import {
  SalesforceIntegrationCancelButtonClickEvent,
  SalesforceIntegrationPublishButtonClickEvent
} from '~src/app/spaces/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { ActionButtonLeftWide, ActionButtons } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { MAX_PROJECT_NAME_LENGTH } from '~src/content/components/CreateProjectDialogBody';
import { Projects } from '~src/projects/resources/Projects';
import { Filter } from '~src/ts/helpers/Filter';
import { IUserInfo } from '~src/ts/models/IUserInfo';
import { FilterFieldNames } from '~src/ts/ServerApi/types/IFilterClause';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { UploadBundleDialogStyles } from '../styles/UploadBundleDialogStyles';

export const ZERO_WIDTH_SPACE = '\u200B';
export const CLOUD_OPTIONS_GROUP_LABEL_ID = 'cloud-options-group-label';
export const PERMISSION_OPTIONS_GROUP_LABEL_ID = 'permission-options-group-label';

export namespace UploadBundlesDialogTestIDs {
  export const CLOUD_OPTION_PREFIX = 'upload-bundles-dialog-bundle-option-';
  export const PROJECT_NAME_INPUT = 'upload-bundles-dialog-project-name';
  export const PROJECT_EXPLORER = 'upload-bundles-dialog-project';
  export const CANCEL = 'upload-bundles-dialog-cancel';
  export const SUBMIT = 'upload-bundles-dialog-submit';
}

// Form Values
export interface UploadBundlesDialogValues {
  bundleIds: string[];
  projectName: string;
  projectDescription: string;
  contentPermissions: string;
}

export interface UploadBundlesDispatchProps {
  uploadBundlesActionDispatch: (values: UploadBundlesDialogValues, oauthId: number, myContentUrl: string) => void;
}

export interface UploadBundlesExternalProps {
  readonly oauthId: number;
  readonly close: () => void;
}

export interface UploadBundlesPropsFromReduxState {
  readonly siteUrls: SiteUrls;
  readonly user: IUserInfo;
}

export enum BundleContentPermissions {
  adminOnly = 'AdminOnly',
  inherited = 'InheritFromParent',
}

enum Clouds {
  salesCloud = 'salesCloud',
  serviceCloud = 'serviceCloud',
}

// type safe identification of the enum members
export type CloudKeyName = keyof typeof Clouds;

const cloudToLabel = (cloud: CloudKeyName): string => {
  switch (cloud) {
    case 'salesCloud': return 'Sales Cloud';
    case 'serviceCloud': return 'Service Cloud';
    default:
      throw new Error ('Unknown cloud type given.');
  }
};

export type UploadBundlesDialogProps = UploadBundlesDispatchProps & UploadBundlesPropsFromReduxState & UploadBundlesExternalProps;
export type InternalUploadBundlesDialogFormProps = UploadBundlesDialogProps & FormikProps<UploadBundlesDialogValues>;

export function InternalUploadBundlesDialogForm(props: InternalUploadBundlesDialogFormProps) {
  // Hardcoded cloud values
  const availableClouds: CloudKeyName[] = [
    'salesCloud',
    'serviceCloud',
  ];

  const [isBusy, setIsBusy] = React.useState<boolean>(false);

  const handleSubmit = () => {
    SalesforceIntegrationPublishButtonClickEvent(props.values.bundleIds);
    setIsBusy(true);

    const { projectName } = props.values;
    getTopLevelProjectsWithName(projectName)
      .then(count => {
        if (count) {
          projectNameAlreadyExists();
        } else {
          projectNameDoesNotExist();
        }
      }).catch(() => {
        // Should only happen if something catastrophic occurs in the backend
        setIsBusy(false);
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.errorUnknown(),
          keepOnLocationChange: true,
        });
      });
  };

  const handleCancel = () => {
    SalesforceIntegrationCancelButtonClickEvent(props.values.bundleIds);
    props.close();
  };

  const projectNameDoesNotExist = () => {
    props.uploadBundlesActionDispatch(props.values, props.oauthId, props.siteUrls.userUrl(props.user.username, props.user.domainName));
    props.close();
  };

  const projectNameAlreadyExists = () => {
    setIsBusy(false);
    props.setFieldError('projectName', Messages.errorProjectNameAlreadyExists());
  };

  const handleCheckboxChange = (cloud: CloudKeyName, checked: boolean) => {
    const bundleIds = [...props.values.bundleIds];
    const index = bundleIds.indexOf(cloud);
    if (checked && index === -1) {
      bundleIds.push(cloud);
    } else if (!checked && index > -1) {
      bundleIds.splice(index, 1);
    }
    props.setFieldValue('bundleIds', bundleIds);
  };

  const handlePermissionChange = (value: string): void => {
    props.setFieldValue('contentPermissions', value);
  };

  return (
    <>
      <section className={UploadBundleDialogStyles.Section}>
        {getSectionHeader(Messages.connectToSalesforce_selectCloudHeader())}
        <div className={UploadBundleDialogStyles.SectionContent}>
          {getSectionDescription(Messages.connectToSalesforce_selectCloudDescription())}
          {getCloudOptions()}
        </div>
      </section>
      <section className={UploadBundleDialogStyles.Section}>
        {getSectionHeader(Messages.connectToSalesforce_createAProjectHeader())}
        <div className={UploadBundleDialogStyles.SectionContent}>
          {getProjectNameField()}
          {getProjectPermissionOptions()}
        </div>
      </section>
      {getActionButtons()}
    </>
  );

  function getSectionHeader(text: string) {
    return <h2 className={UploadBundleDialogStyles.SectionHeader}>{text}</h2>;
  }

  function getSectionDescription(text: string) {
    return <p className={UploadBundleDialogStyles.SectionDescription}>{text}</p>;
  }

  function getCloudOptions() {
    return <>
      <div id={CLOUD_OPTIONS_GROUP_LABEL_ID}>{Messages.connectToSalesforce_clouds()}</div>
      <CheckBoxWithLabelGroupWidget aria-labelledby={CLOUD_OPTIONS_GROUP_LABEL_ID}>
        {availableClouds.map(cloud =>
          <CloudOption
            key={cloud}
            cloud={cloud}
            handleChange={handleCheckboxChange}
          />
        )}
      </CheckBoxWithLabelGroupWidget>
    </>;
  }

  function getProjectNameField() {
    return <TextFieldWidget
      className={UploadBundleDialogStyles.TextField}
      kind={'line'}
      label={Messages.connectToSalesforce_newProjectName()}
      maxLength={MAX_PROJECT_NAME_LENGTH}
      message={props.errors.projectName ?? ZERO_WIDTH_SPACE}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        props.setFieldValue('projectName', e.target.value);
      }}
      onClear={() => props.setFieldValue('projectName', '')}
      style={UploadBundleDialogStyles.FullWidthStyle}
      testId={UploadBundlesDialogTestIDs.PROJECT_NAME_INPUT}
      value={props.values.projectName}
      valid={props.errors.projectName ? false : undefined}
    />;
  }

  function getProjectPermissionOptions() {
    return <ProjectPermissionOptions
      handleChange={handlePermissionChange}
    />;
  }

  function getActionButtons() {
    return <div className={ActionButtons}>
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId={UploadBundlesDialogTestIDs.CANCEL}
        children={Messages.cancel()}
        handleClick={handleCancel}
      />
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          disabled={!props.isValid}
          buttonType={ButtonType.Primary}
          showSpinner={isBusy}
          testId={UploadBundlesDialogTestIDs.SUBMIT}
          title={Messages.publish()}
          clicked={handleSubmit}
          onDarkBackground={true}
        />
      </span>
    </div>;
  }
}

interface PermissionsOptionProps {
  readonly handleChange: (permission: BundleContentPermissions) => void;
}

function ProjectPermissionOptions(props: PermissionsOptionProps) {
  const [index, setIndex] = React.useState<number>(0);

  const buttonList = [
    {
      label: Messages.connectToSalesforce_projectPermissionPrivate(),
      testId: 'admin-only-option',
      value: BundleContentPermissions.adminOnly,
    },
    {
      label: Messages.connectToSalesforce_projectPermissionInherit(),
      testId: 'inherited-option',
      value: BundleContentPermissions.inherited,
    },
  ];
  return <>
    <div id={PERMISSION_OPTIONS_GROUP_LABEL_ID}>{Messages.connectToSalesforce_projectPermissions()}</div>
    <RadioButtonWithLabelGroupWidget
      aria-labelledby={PERMISSION_OPTIONS_GROUP_LABEL_ID}
      buttonList={buttonList}
      checkedIndex={index}
      handleChange={(value, index) => {
        props.handleChange(value);
        setIndex(index);
      }}
    />
  </>;
}

interface CloudOptionProps {
  readonly cloud: CloudKeyName;
  readonly handleChange: (cloud: CloudKeyName, checked: boolean) => void;
}

function CloudOption(props: CloudOptionProps) {
  const [checked, setChecked] = React.useState<boolean>(false);

  return <CheckBoxWithLabelWidget
    checked={checked}
    handleChange={(checked) => {
      props.handleChange(props.cloud, checked);
      setChecked(checked);
    }}
    testId={UploadBundlesDialogTestIDs.CLOUD_OPTION_PREFIX + props.cloud}
    label={cloudToLabel(props.cloud)}
    disabled={false} />;
}

function getTopLevelProjectsWithName(projectName: string): Promise<number> {
  return Projects.all()
    .withFilters([Filter.fieldEq(FilterFieldNames.topLevelProject, true),
                  Filter.fieldEq(FilterFieldNames.name, projectName)])
    .getCount();
}

interface UploadBundlesDialogErrors {
  bundleIds?: string;
  projectName?: string;
}

function validateFormFields(values: UploadBundlesDialogValues): UploadBundlesDialogErrors {
  const errors: UploadBundlesDialogErrors = {};

  if (_.isEmpty(values.bundleIds)) {
    errors.bundleIds = 'No Cloud Chosen'; // not shown to user, submit button is disabled
  }

  if (_.isEmpty(values.projectName)) {
    errors.projectName = Messages.connectToSalesforce_projectNameBlankError();
  }

  return errors;
}

export const UploadBundlesDialog = withFormik<UploadBundlesDialogProps, UploadBundlesDialogValues>({
  mapPropsToValues: (props) => {
    return {
      bundleIds: [],
      projectName: 'Salesforce',
      projectDescription: Messages.connectToSalesforce_projectDescription({
        SCHEDULE_REFRESHES_LINK_TEXT: Messages.connectToSalesforce_projectDescriptionScheduleRefreshesLinkText(),
        SCHEDULE_REFRESHES_HELP_LINK: HelpService.getHelpUrlByCategory('schedule-refreshes'),
        PERMISSIONS_LINK_TEXT: Messages.connectToSalesforce_projectDescriptionPermissionsLinkText(),
        PERMISSIONS_HELP_LINK: HelpService.getHelpUrlByCategory('server-permissions'),
      }),
      contentPermissions: BundleContentPermissions.adminOnly,
    };
  },
  validate: (values) => validateFormFields(values),
  validateOnMount: true,
  handleSubmit: (values, actions) => {}
})(InternalUploadBundlesDialogForm);
